import { useAppSelector } from "src/common/state/hooks";
import { useEffect, useRef, useState } from "react";
import { loadingSelector, LoadingState } from "../../state/slice/appStateSlice";
import type { RootState } from "../../state/store";
import Spinner from "../Loader/Spinner";
import { Success } from "./Success";

export const useLoading = (componentId: string) => {
  const { loading } = useAppSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, componentId),
    };
  });

  return loading;
};

const spinnerStartMap = new Map<string, number>();

/**
 * Display a Loader if app is loading and a success message if asyncChunk succeeded
 * @param componentId
 * @param children (optional): Display only if appState is Success or Idle
 * @param showSuccess (optional): Show "success" message after spin
 * @param spinnerPlaceholder (optional): Spinner replaces the child component
 * @param spinnerStyle (optional): Override spinner style
 * @param spinForDev (optional): Force spinner for development
 * @param minimalSpinnerDuration (optional): Minimum spinner time (ms)
 */

export const IsLoading = ({
  componentId,
  children,
  showSuccess = true,
  spinnerPlaceholder = false,
  spinnerStyle = {},
  spinForDev = false,
  minimalSpinnerDuration,
}: {
  componentId: string;
  children?: JSX.Element | JSX.Element[];
  showSuccess?: boolean;
  spinnerPlaceholder?: boolean;
  spinnerStyle?: React.CSSProperties;
  spinForDev?: boolean;
  minimalSpinnerDuration?: number;
}) => {
  const loading = useLoading(componentId);
  const [visibleLoading, setVisibleLoading] = useState(loading);
  const startTimeRef = useRef<number | null>(spinnerStartMap.get(componentId) ?? null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const now = Date.now();

    if (loading === LoadingState.Loading) {
      if (startTimeRef.current == null) {
        startTimeRef.current = now;
        spinnerStartMap.set(componentId, now);
      }
      setVisibleLoading(LoadingState.Loading);
    } else {
      const minMs = minimalSpinnerDuration ?? 0;
      const startedAt = startTimeRef.current ?? spinnerStartMap.get(componentId) ?? null;

      if (startedAt && minMs > 0) {
        const elapsed = now - startedAt;
        const remaining = minMs - elapsed;

        if (remaining > 0) {
          timeoutId = setTimeout(() => {
            setVisibleLoading(loading);
            startTimeRef.current = null;
            spinnerStartMap.delete(componentId);
          }, remaining);
        } else {
          setVisibleLoading(loading);
          startTimeRef.current = null;
          spinnerStartMap.delete(componentId);
        }
      } else {
        setVisibleLoading(loading);
        startTimeRef.current = null;
        spinnerStartMap.delete(componentId);
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, minimalSpinnerDuration, componentId]);

  if (!spinForDev && visibleLoading === LoadingState.Success && showSuccess) {
    return <Success />;
  }

  if (spinForDev || visibleLoading === LoadingState.Loading) {
    return (
      <div className={spinnerPlaceholder ? "flex justify-center items-center w-full h-full" : ""}>
        <Spinner relative={spinnerPlaceholder} style={spinnerStyle} />
      </div>
    );
  }

  if (children) {
    return <>{children}</>;
  }

  return <div></div>;
};
